import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import {
  FormRowLabel,
  FormRowLayout,
  FormRowValue,
} from '@/common/components/FormRow/FormRow.tsx'
import { Disclosure, DisclosureButton } from '@headlessui/react'
import { MinusIcon, PlusIcon } from '@/common/icons/arrows.tsx'
import Image from '@/common/components/Image.tsx'
import { useApplicationDocumentLogging } from '@/pages/applicants/applicant_details/hooks/useApplicationDocumentLogging.ts'
import { FamilyApplicationDetails } from '@/queries/family_applications/requests.ts'
import React from 'react'

interface Props {
  application?: FamilyApplicationDetails | null
}

const HouseHoldDetails: React.FC<Props> = ({ application }) => {
  const logDocView = useApplicationDocumentLogging({
    logType: 'family',
    applicantId: application?.user_id ?? null,
    documentsViewed: application?.family_documents,
    docsViewedType: 'TAX',
  })

  function capitalizeAndRemoveUnderscores(text: string | null) {
    if (!text) {
      return ''
    }

    return text
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  return (
    <Card>
      <CardHeader className="font-medium text-gray-800">
        Household Details
      </CardHeader>
      <CardContent>
        <FormRowLayout>
          <FormRowLabel>Household Size</FormRowLabel>
          <FormRowValue>{application?.household_inhabitants}</FormRowValue>
        </FormRowLayout>

        <FormRowLayout>
          <FormRowLabel>2024 Income Type</FormRowLabel>
          <FormRowValue>
            {capitalizeAndRemoveUnderscores(
              application?.household_income_type ?? null
            )}
          </FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>2024 Income</FormRowLabel>
          <FormRowValue>
            {capitalizeAndRemoveUnderscores(
              application?.household_filed_federal_income ?? null
            )}
          </FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Household Income Greater Than Threshold</FormRowLabel>
          <FormRowValue>
            {application?.household_income_greater ? 'Yes' : 'No'}
          </FormRowValue>
        </FormRowLayout>
        <FormRowLayout>
          <FormRowLabel>Non Taxable Income</FormRowLabel>
          <FormRowValue>
            {application?.non_taxable_income?.map((item) =>
              capitalizeAndRemoveUnderscores(item)
            )}
          </FormRowValue>
        </FormRowLayout>

        <FormRowLayout className="flex-col items-start border-b-0">
          <Disclosure>
            {({ open }) => {
              if (open) {
                logDocView()
              }
              return (
                <>
                  <div className="flex flex-row w-full items-center justify-between">
                    <FormRowLabel>Income Documents</FormRowLabel>
                    <DisclosureButton className="flex items-center">
                      <span className="text-sm text-gray-400">
                        {open ? <MinusIcon /> : <PlusIcon />}
                      </span>
                    </DisclosureButton>
                  </div>
                  {open && (
                    <div className="grid lg:grid-cols-3 gap-4 mt-8">
                      {application?.family_documents
                        ?.filter((document) => document.type === 'TAX')
                        .map((document) => {
                          return (
                            <Image
                              allowZoom={true}
                              path={document?.image_name_front}
                              width={'100%'}
                              alt={document.variation}
                            />
                          )
                        })}
                    </div>
                  )}
                </>
              )
            }}
          </Disclosure>
        </FormRowLayout>
      </CardContent>
    </Card>
  )
}
export default HouseHoldDetails
